import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { IS_DEMO } from "@/utils/env";
import { getOktaAuth } from "@/utils/oktaAuth";
import { useAuthStore } from "@/stores/authStore";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    redirect: { name: "gem-list" },
  },
  // ── Auth callback route (production only) ─────────────────────────────────
  {
    path: "/login/callback",
    name: "login-callback",
    component: () => import("@/views/LoginCallbackView.vue"),
    meta: { title: "Signing In…", public: true },
  },
  // ── Application routes ────────────────────────────────────────────────────
  {
    path: "/gems",
    name: "gem-list",
    component: () => import("@/views/GemListView.vue"),
    meta: {
      title: "",
      hasHero: true,
    },
  },
  {
    path: "/favorite-gems",
    name: "favorite-gems",
    component: () => import("@/views/FavoriteGemsView.vue"),
    meta: {
      title: "Favorieten",
      hasHero: true,
    },
  },
  {
    path: "/gems/:id",
    name: "gem-detail",
    component: () => import("@/views/GemDetailView.vue"),
    meta: {
      title: "Gem Details",
    },
  },
  {
    path: "/gems/:id/edit",
    name: "gem-edit",
    component: () => import("@/views/GemFormView.vue"),
    meta: {
      title: "Gem Aanpassen",
    },
  },
  {
    path: "/gems/create",
    name: "gem-create",
    component: () => import("@/views/GemFormView.vue"),
    meta: {
      title: "Gem Aanmaken",
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundView.vue"),
    meta: {
      title: "Page Not Found",
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

router.beforeEach(async (to) => {
  // Update document title.
  const defaultTitle = "Gems Library";
  const pageTitle = to.meta.title;
  document.title = pageTitle ? `${defaultTitle} | ${pageTitle}` : defaultTitle;

  // In demo mode all routes are always accessible.
  if (IS_DEMO) return true;

  // Routes marked as public (login, callback) are always accessible.
  if (to.meta.public) return true;

  // Production mode: require Okta authentication – redirect straight to the
  // Okta-hosted sign-in page (no custom login view).
  try {
    const isAuthenticated = await getOktaAuth().isAuthenticated();
    if (!isAuthenticated) {
      console.log("[Auth] User not authenticated, redirecting to Okta sign-in");
      await getOktaAuth().signInWithRedirect({ originalUri: to.fullPath });
      return false; // cancel navigation while the browser redirects to Okta
    }
  } catch (error) {
    console.error("[Auth] Error checking authentication status:", error);
    throw error;
  }

  // Ensure the auth store is populated (handles page-refresh without re-login).
  const authStore = useAuthStore();
  if (!authStore.currentUser) {
    try {
      const userInfo = await getOktaAuth().getUser();
      authStore.setUser({
        id: userInfo.sub,
        name:
          [userInfo.given_name, userInfo.family_name]
            .filter(Boolean)
            .join(" ") ||
          userInfo.name ||
          userInfo.preferred_username ||
          userInfo.email ||
          userInfo.sub,
        email: userInfo.email ?? userInfo.sub,
      });
    } catch (error) {
      console.error("[Auth] Error fetching user info:", error);
      // Clear any stale state and redirect to sign-in
      authStore.clearUser();
      await getOktaAuth().signInWithRedirect({ originalUri: to.fullPath });
      return false;
    }
  }

  return true;
});

export default router;
